import { ref, readonly } from 'vue';

export interface ToastOptions {
  message: string;
  title?: string;
  variant?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'danger'
    | 'warning'
    | 'info'
    | 'light'
    | 'dark';
  duration?: number;
  html?: boolean;
  closable?: boolean;
  position?: ToastPosition;
}

export type ToastPosition =
  | 'top-start'
  | 'top-center'
  | 'top-end'
  | 'middle-start'
  | 'middle-center'
  | 'middle-end'
  | 'bottom-start'
  | 'bottom-center'
  | 'bottom-end';

export interface Toast extends Required<ToastOptions> {
  id: string;
  createdAt: number;
}

const toasts = ref<Toast[]>([]);
const timers = new Map<string, number>();
let idCounter = 0;

export function useToast() {
  const defaultOptions: Required<ToastOptions> = {
    message: '',
    title: '',
    variant: 'info',
    duration: 5000,
    html: false,
    closable: true,
    position: 'top-end',
  };

  function show(options: ToastOptions | string): string {
    const opts: ToastOptions =
      typeof options === 'string' ? { message: options } : options;

    const toast: Toast = {
      ...defaultOptions,
      ...opts,
      id: `toast-${++idCounter}`,
      createdAt: Date.now(),
    };

    toasts.value.push(toast);

    if (toast.duration > 0) {
      const handle = window.setTimeout(() => remove(toast.id), toast.duration);
      timers.set(toast.id, handle);
    }
    return toast.id;
  }

  function remove(id: string) {
    const handle = timers.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.delete(id);
    }
    const index = toasts.value.findIndex((toast) => toast.id === id);
    if (index !== -1) {
      toasts.value.splice(index, 1);
    }
  }

  function clear() {
    timers.forEach((timerId) => clearTimeout(timerId));
    timers.clear();
    toasts.value = [];
  }

  function success(message: string, options?: Partial<ToastOptions>): string {
    return show({
      message,
      variant: 'success',
      ...options,
    });
  }

  function error(message: string, options?: Partial<ToastOptions>): string {
    return show({
      message,
      variant: 'danger',
      ...options,
    });
  }

  function warning(message: string, options?: Partial<ToastOptions>): string {
    return show({
      message,
      variant: 'warning',
      ...options,
    });
  }

  function info(message: string, options?: Partial<ToastOptions>): string {
    return show({
      message,
      variant: 'info',
      ...options,
    });
  }

  return {
    toasts: readonly(toasts),
    show,
    remove,
    clear,
    success,
    error,
    warning,
    info,
  };
}
